import type { InspectionTemplateBody } from '@ayumia/contracts';
import { layoutOf, placementOf } from '@ayumia/component-runtime';
import { text } from '../../app/i18n';
import type { Entry, Section } from '../form-targets';

/**
 * Lecture de la présentation 1.10.0 d'un formulaire (ADR-146 addendum (16)) : bandeau réglable, pied
 * masquable, bande de champs, liste et détail, visuels des options, apparence d'une valeur manquante.
 * Calculs purs, partagés par les composants web ; la tablette les porte à l'identique
 * (`form_presentation.dart`). Présentation seulement : rien n'autorise ni ne valide.
 */

type Template = InspectionTemplateBody;
type Localized = Readonly<Record<string, string>> | undefined;
export type Presentation = NonNullable<Entry['presentation']>;
export type MediaRef = { file_id?: unknown; common_key?: string; alt?: Localized };
export type OptionColor = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';
export type OptionVisual = 'none' | 'icon' | 'dot' | 'image' | 'object_image';

export function presentationOf(entry: Entry): Presentation {
  return (entry.presentation ?? {}) as Presentation;
}

/** Le gabarit déclare-t-il la présentation 1.10.0 (bandeau réglable, bande, liste et détail) ? */
export function usesFormV2(template: Template): boolean {
  const layout = layoutOf(template);
  if (layout === undefined) return false;
  if (layout.header !== undefined) return true;
  return Object.values(layout.placements ?? {}).some((placement) => placement.presentation?.as === 'list_detail' || placement.region === 'strip');
}

// ---- Bandeau et pied

export interface HeaderSettings {
  overline: string;
  showVisual: boolean;
  date: 'datetime' | 'date' | 'none';
  operator: 'photo_and_name' | 'name' | 'none';
  environmentBadge: boolean;
  submit: { show: 'always' | 'before_last_page' | 'never'; appearance: 'link' | 'secondary' | 'primary'; label: string; icon: string | undefined; nextStyle: 'demote' | 'keep' };
}

/** Réglages du bandeau, valeurs par défaut comprises (raccourci « Valider » discret avant la dernière page). */
export function headerSettings(template: Template): HeaderSettings {
  const header = layoutOf(template)?.header ?? {};
  const shortcut = header.submit_shortcut ?? {};
  return {
    overline: text(header.overline as Localized, 'fr', ''),
    showVisual: header.show_visual !== false,
    date: header.date ?? 'date',
    operator: header.operator ?? 'name',
    environmentBadge: header.environment_badge === true,
    submit: {
      show: shortcut.show ?? 'before_last_page',
      appearance: shortcut.appearance ?? 'link',
      label: text(shortcut.label as Localized, 'fr', 'Valider'),
      icon: shortcut.icon,
      nextStyle: shortcut.next_style ?? 'keep',
    },
  };
}

/** Le raccourci « Valider » du bandeau est-il visible sur cette page ? */
export function submitShortcutShown(settings: HeaderSettings, lastPage: boolean, footerHidden: boolean): boolean {
  // Pied masqué : le bandeau porte la seule action de validation (`LAYOUT_SUBMIT_UNREACHABLE` sinon).
  if (footerHidden) return true;
  if (settings.submit.show === 'never') return false;
  if (settings.submit.show === 'always') return true;
  return !lastPage;
}

export function footerHidden(template: Template): boolean {
  return layoutOf(template)?.footer?.hidden === true;
}

// ---- Valeur manquante

/**
 * Texte d'une valeur manquante (`empty_display`) : tirets, rien, le texte d'aide (`placeholder`), ou une
 * invite « Libellé ? » (montrée en couleur d'alerte par l'appelant).
 */
/**
 * Libellé court d'un champ dans une cellule ou un en-tête de colonne : le plus court du libellé et du texte
 * d'aide (« État » plutôt que « État à la réception »).
 */
export function shortLabel(entry: Entry): string {
  const label = text(entry.field.label as Localized, 'fr', entry.field.field_id);
  const placeholder = text(presentationOf(entry).placeholder as Localized, 'fr', '');
  return placeholder !== '' && placeholder.length < label.length ? placeholder : label;
}

/**
 * En-tête de colonne d'une liste (inspection-template-body 1.12.0, `column_labels`, ADR-146 addendum (21)) :
 * le libellé court déclaré pour la colonne (« POIDS »), sinon le libellé court du champ.
 */
export function columnLabel(labels: Readonly<Record<string, Localized>> | undefined, entry: Entry): string {
  const declared = text(labels?.[entry.field.field_id], 'fr', '');
  return declared !== '' ? declared : shortLabel(entry);
}

function promptOf(label: string): string {
  return label.trimEnd().endsWith('?') ? label : `${label} ?`;
}

export function emptyText(entry: Entry, where: 'cell' | 'control' = 'control'): { text: string; tone: 'muted' | 'prompt' } {
  const presentation = presentationOf(entry);
  const display = presentation.empty_display ?? (where === 'cell' ? 'dashes' : 'placeholder');
  const placeholder = text(presentation.placeholder as Localized, 'fr', '');
  if (display === 'blank') return { text: '', tone: 'muted' };
  if (display === 'dashes') return { text: '—', tone: 'muted' };
  // Le libellé court peut être le texte d'invite déclaré (« État ? ») : jamais deux points d'interrogation.
  if (display === 'prompt') return where === 'cell' ? { text: promptOf(shortLabel(entry)), tone: 'prompt' } : { text: placeholder === '' ? `${text(entry.field.label as Localized, 'fr', entry.field.field_id)} ?` : placeholder, tone: 'prompt' };
  return { text: placeholder === '' ? '—' : placeholder, tone: 'muted' };
}

// ---- Options et visuels

export interface ChoiceOption {
  value: string;
  label: string;
  icon: string | undefined;
  color: OptionColor | undefined;
  image: MediaRef | undefined;
}

/** Options d'un choix dans l'ordre déclaré (`options.<valeur>.order`, sinon l'ordre des valeurs). */
export function choiceOptions(entry: Entry): ChoiceOption[] {
  const presentation = presentationOf(entry);
  const validation = entry.field.validation as { enum_values?: string[]; enum_labels?: Record<string, Localized> } | undefined;
  const values = entry.field.type === 'boolean' ? ['true', 'false'] : [...(validation?.enum_values ?? [])];
  const declared = (presentation.options ?? {}) as Record<string, { image?: MediaRef; icon?: string; color?: OptionColor; order?: number }>;
  const legacyIcons = (presentation.option_icons ?? {}) as Record<string, string>;
  return values
    .map((value, index) => ({ value, index, settings: declared[value] ?? {} }))
    .sort((a, b) => (a.settings.order ?? a.index) - (b.settings.order ?? b.index))
    .map(({ value, settings }) => ({
      value,
      label: entry.field.type === 'boolean' ? (value === 'true' ? 'Oui' : 'Non') : text(validation?.enum_labels?.[value], 'fr', value),
      icon: settings.icon ?? legacyIcons[value],
      color: settings.color,
      image: settings.image,
    }));
}

/** Visuel des options : déclaré, sinon une icône si des icônes sont déclarées, sinon rien. */
export function optionVisual(entry: Entry): OptionVisual {
  const presentation = presentationOf(entry);
  if (presentation.option_visual !== undefined) return presentation.option_visual;
  return Object.keys(presentation.option_icons ?? {}).length > 0 ? 'icon' : 'none';
}

/** Clé de la source d'images d'une option « image de l'objet » (`Type.champ`), sinon le champ lui-même. */
export function optionSourceKey(entry: Entry): string | undefined {
  const source = presentationOf(entry).option_source;
  return source === undefined ? undefined : `${source.object_type}.${source.field_id}`;
}

// ---- Largeur en colonnes

/** Largeur d'un champ placé, en colonnes sur 12 (`presentation.layout.width`) ; pleine largeur par défaut. */
export function columnSpan(entry: Entry): number {
  const layout = presentationOf(entry).layout;
  if (layout?.span === 'full') return 12;
  const width = layout?.width;
  return typeof width === 'number' && width >= 1 && width <= 12 ? Math.round(width) : 12;
}

// ---- Liste et détail

export type ListDetailPlacement = ReturnType<typeof placementOf> & { presentation: { as: 'list_detail' } };

export function isListDetail(template: Template, section: Section): boolean {
  return section.series !== undefined && placementOf(template, section.section_id).presentation?.as === 'list_detail';
}

export function inStrip(template: Template, section: Section, stripRegions: ReadonlySet<string>): boolean {
  const region = placementOf(template, section.section_id).region;
  return region !== undefined && stripRegions.has(region) && section.series === undefined && section.repeat === undefined;
}

/** Colonnes de la liste : celles du registre (`series.ledger.columns`), sinon tous les champs saisis. */
export function listColumns(section: Section): Entry[] {
  const declared = (section.series as { ledger?: { columns?: string[] } } | undefined)?.ledger?.columns;
  const fields = section.fields.filter((entry) => entry.compute === undefined);
  if (declared === undefined || declared.length === 0) return fields;
  return declared.map((id) => fields.find((entry) => entry.field.field_id === id)).filter((entry): entry is Entry => entry !== undefined);
}

const WIDTHS = { narrow: '0.4fr', medium: '1fr', wide: '1.15fr' } as const;

/** Gabarit de grille de la liste : n° de ligne, puis la largeur déclarée de chaque colonne. */
export function listGrid(columns: readonly Entry[], widths: Readonly<Record<string, 'narrow' | 'medium' | 'wide'>> = {}, rowNumber = true): string {
  const cells = columns.map((entry) => `minmax(0, ${WIDTHS[widths[entry.field.field_id] ?? (entry.field.type === 'number' ? 'narrow' : 'medium')]})`);
  return [...(rowNumber ? ['2.5rem'] : []), ...cells].join(' ');
}

/** « Carcasse 1 / 3 » : titre déclaré et position de la ligne en cours. */
export function panelPosition(title: string, current: number, total: number, showPosition: boolean): string {
  if (!showPosition) return title;
  const position = `${current + 1} / ${Math.max(total, current + 1)}`;
  return title === '' ? position : `${title} ${position}`;
}

/**
 * Une ligne sans aucune réponse : elle ne part pas (la ligne nouvelle ouverte d'office). `kept` : champs repris de la
 * ligne précédente (`series.ledger.keep_fields`, l'« inherit » du v1) — une ligne qui n'a que ceux-là reste vide.
 */
export function rowIsEmpty(row: Readonly<Record<string, unknown>>, kept: readonly string[] = []): boolean {
  return Object.entries(row).every(([key, value]) => kept.includes(key) || value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0));
}

/** Champs d'une série repris de la ligne précédente à l'ajout (`series.ledger.keep_fields`). */
export function keptFields(section: { series?: unknown }): readonly string[] {
  const keep = (section.series as { ledger?: { keep_fields?: unknown } } | undefined)?.ledger?.keep_fields;
  return Array.isArray(keep) ? keep.filter((id): id is string => typeof id === 'string') : [];
}

/** Nombre affiché avec la virgule et les décimales déclarées (`presentation.decimals`). */
export function numberShown(value: unknown, decimals: number | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  const fixed = decimals === undefined ? String(value) : value.toFixed(Math.max(0, Math.min(6, decimals)));
  return fixed.replace('.', ',');
}
